import { useState } from "react";
import { toast } from "sonner";
import { api, formatEUR } from "@/lib/api";

export const STATUS_META = {
  previsionnelle: { label: "Prévisionnelle", cls: "pill-gray" },
  acquise: { label: "Acquise", cls: "pill-blue" },
  partiellement_payee: { label: "Partiellement payée", cls: "pill-orange" },
  payee: { label: "Payée", cls: "pill-green" },
  contestee: { label: "Contestée", cls: "pill-red" },
};

export function StatusPill({ status, testid }) {
  const m = STATUS_META[status] || { label: status, cls: "pill-gray" };
  return <span className={`pill ${m.cls}`} data-testid={testid}>{m.label}</span>;
}

/** Formulaire d'enregistrement d'un paiement (jamais déclenché : simple saisie). */
export function PaymentForm({ commission, onDone, onCancel }) {
  const open = commission.lines.filter((l) => l.reste > 0.004);
  const [form, setForm] = useState({ line_key: open[0]?.key || "", montant: open[0] ? String(open[0].reste) : "", date: new Date().toISOString().slice(0, 10), reference: "" });
  const [busy, setBusy] = useState(false);
  const line = commission.lines.find((l) => l.key === form.line_key);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/commissions/${commission.id}/paiements`, { ...form, montant: Number(String(form.montant).replace(",", ".")) });
      toast.success("Paiement enregistré");
      onDone(r.data.commission);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enregistrement impossible");
    } finally { setBusy(false); }
  };
  if (!open.length) return <div className="text-sm text-slate-500" data-testid="payment-form-empty">Toutes les lignes sont soldées.</div>;
  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end" data-testid="payment-form">
      <label className="text-xs text-slate-500 sm:col-span-2">Bénéficiaire
        <select className="input-base w-full mt-1" value={form.line_key} onChange={(e) => { const l = open.find((x) => x.key === e.target.value); setForm({ ...form, line_key: e.target.value, montant: l ? String(l.reste) : "" }); }} data-testid="payment-line-select">
          {open.map((l) => <option key={l.key} value={l.key}>{l.label} — {l.beneficiaire} (reste {formatEUR(l.reste)})</option>)}
        </select>
      </label>
      <label className="text-xs text-slate-500">Montant (€)
        <input className="input-base w-full mt-1 tabular-nums" inputMode="decimal" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} data-testid="payment-amount-input" />
      </label>
      <label className="text-xs text-slate-500">Date
        <input type="date" className="input-base w-full mt-1" value={form.date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date-input" />
      </label>
      <label className="text-xs text-slate-500">Référence
        <input className="input-base w-full mt-1" placeholder="Virement, n° facture…" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-ref-input" />
      </label>
      <div className="sm:col-span-5 flex gap-2 justify-end">
        {onCancel && <button type="button" className="btn-secondary" onClick={onCancel} data-testid="payment-cancel-btn">Annuler</button>}
        <button type="submit" className="btn-primary" disabled={busy || !line} data-testid="payment-submit-btn">Enregistrer le paiement</button>
      </div>
    </form>
  );
}

export function CommissionLines({ commission, testid = "commission-lines" }) {
  return (
    <table className="w-full text-[13px]" data-testid={testid}>
      <thead><tr className="text-left text-slate-500"><th className="font-semibold py-1">Ligne</th><th className="font-semibold py-1">Bénéficiaire</th><th className="font-semibold py-1 text-right">Montant</th><th className="font-semibold py-1 text-right">Payé</th><th className="font-semibold py-1 text-right">Reste</th></tr></thead>
      <tbody>
        {commission.lines.map((l) => (
          <tr key={l.key} className="border-t border-slate-100" data-testid={`${testid}-${l.key}`}>
            <td className="py-1.5 font-semibold text-slate-800">{l.label}</td><td className="py-1.5 text-slate-600 truncate max-w-[180px]">{l.beneficiaire}</td>
            <td className="py-1.5 text-right tabular-nums font-bold">{formatEUR(l.montant)}</td><td className="py-1.5 text-right tabular-nums text-[color:var(--cf-success-text)]">{formatEUR(l.paye)}</td><td className="py-1.5 text-right tabular-nums">{formatEUR(l.reste)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CommissionHistory({ commission }) {
  const [open, setOpen] = useState(false);
  const h = commission.history || [];
  return (
    <div data-testid="commission-history">
      <button type="button" className="text-xs font-semibold text-slate-500 hover:text-slate-900" onClick={() => setOpen(!open)} data-testid="commission-history-toggle">{open ? "Masquer" : "Afficher"} l'historique ({h.length})</button>
      {open && (
        <ul className="mt-2 space-y-1 text-[12px] text-slate-600">
          {[...h].reverse().map((e, i) => <li key={i} className="flex gap-2"><span className="text-slate-400 tabular-nums shrink-0">{(e.at || "").slice(0, 16).replace("T", " ")}</span><span className="font-semibold">{e.action}</span><span className="text-slate-400">· {e.by}</span>{e.details?.motif && <span>— {e.details.motif}</span>}</li>)}
        </ul>
      )}
    </div>
  );
}

export function MotifAction({ label, endpoint, onDone, testid, tone = "secondary" }) {
  const [motif, setMotif] = useState("");
  const [open, setOpen] = useState(false);
  const run = async () => {
    try { const r = await api.post(endpoint, { motif }); toast.success(label + " : fait"); setOpen(false); setMotif(""); onDone(r.data.commission); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
  };
  if (!open) return <button type="button" className={tone === "danger" ? "btn-secondary text-[color:var(--cf-danger-text)]" : "btn-secondary"} onClick={() => setOpen(true)} data-testid={`${testid}-btn`}>{label}</button>;
  return (
    <div className="flex gap-2 items-center" data-testid={`${testid}-form`}>
      <input className="input-base flex-1" placeholder="Motif (obligatoire, tracé)" value={motif} onChange={(e) => setMotif(e.target.value)} data-testid={`${testid}-motif-input`} />
      <button type="button" className="btn-primary" disabled={motif.trim().length < 3} onClick={run} data-testid={`${testid}-confirm-btn`}>Confirmer</button>
      <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Annuler</button>
    </div>
  );
}
